
import { Component, Input, forwardRef, OnInit, ElementRef, HostListener, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { COUNTRIES, Country } from './countries';

@Component({
    selector: 'app-isd-picker',
    templateUrl: './isd-picker.component.html',
    styleUrls: ['./isd-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => IsdPickerComponent),
            multi: true
        }
    ]
})
export class IsdPickerComponent implements ControlValueAccessor, OnInit, OnChanges {
    @Input() preferredCountries: string[] = [];
    @Input() defaultDialCode: string;
    @Input() maxLength: number = 15;

    countries: Country[] = [];
    filteredCountries: Country[] = [];
    selectedCountry: Country;

    phoneNumber: string = '';
    isDisabled: boolean = false;
    isOpen: boolean = false;
    searchText: string = '';

    onChange: any = () => { };
    onTouched: any = () => { };

    @ViewChild('dropdown') dropdown: ElementRef;

    constructor(private eRef: ElementRef) { }

    ngOnInit(): void {
        // Sort preferred countries to top if needed, otherwise just load all
        this.countries = [...COUNTRIES];
        if (this.preferredCountries.length > 0) {
            const preferred = this.countries.filter(c => this.preferredCountries.includes(c.iso2));
            const others = this.countries.filter(c => !this.preferredCountries.includes(c.iso2));
            this.countries = [...preferred, ...others];
        }
        this.filteredCountries = this.countries;

        this.setDefaultCountry();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.defaultDialCode && !changes.defaultDialCode.firstChange) {
            this.setDefaultCountry();
        }
    }

    setDefaultCountry() {
        if (this.defaultDialCode) {
            // Remove + if present
            const cleanCode = this.defaultDialCode.replace('+', '');

            if (!this.selectedCountry) {
                const defaultC = this.countries.find(c => c.dialCode === cleanCode);
                if (defaultC) {
                    this.selectedCountry = defaultC;
                } else {
                    // Fallback to IN if not found and not set
                    this.selectedCountry = this.countries.find(c => c.iso2 === 'IN') || this.countries[0];
                }
            } else if (!this.phoneNumber) {
                // If selectedCountry is set but no phone number, update to default
                const defaultC = this.countries.find(c => c.dialCode === cleanCode);
                if (defaultC) {
                    this.selectedCountry = defaultC;
                }
            }
        } else if (!this.selectedCountry) {
            // No default code and no selected country -> fallback
            this.selectedCountry = this.countries.find(c => c.iso2 === 'IN') || this.countries[0];
        }
    }

    // ControlValueAccessor methods
    writeValue(value: any): void {
        if (value) {
            if (typeof value === 'object') {
                this.phoneNumber = value.nationalNumber || '';
                // If we wanted to sync country, we could look it up by dialCode or countryCode
                if (value.countryCode) {
                    const country = this.countries.find(c => c.iso2 === value.countryCode);
                    if (country) {
                        this.selectedCountry = country;
                    }
                } else if (value.dialCode) {
                    const country = this.countries.find(c => c.dialCode === value.dialCode);
                    if (country) {
                        this.selectedCountry = country;
                    }
                }
            } else {
                this.phoneNumber = value;
            }
        } else {
            this.phoneNumber = '';
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    // UI Actions
    toggleDropdown() {
        if (!this.isDisabled) {
            this.isOpen = !this.isOpen;
            if (this.isOpen) {
                this.searchText = '';
                this.filterCountries();
            }
        }
    }

    selectCountry(country: Country) {
        this.selectedCountry = country;
        this.isOpen = false;
        this.propagateChange();
    }

    onPhoneNumberChange(value: string) {
        this.phoneNumber = value;
        this.propagateChange();
    }

    propagateChange() {
        if (!this.phoneNumber) {
            this.onChange(null);
            return;
        }
        // Create output similar to ngx-intl-tel-input if possible, 
        // or just a custom object that the parent component can use.
        // Based on create-vendor parent usage: 
        // event.dialCode and event.nationalNumber are used.
        const output = {
            dialCode: this.selectedCountry.dialCode,
            nationalNumber: this.phoneNumber,
            // properties for compatibility if needed
            number: '+' + this.selectedCountry.dialCode + this.phoneNumber,
            countryCode: this.selectedCountry.iso2
        };
        this.onChange(output);
    }

    filterCountries() {
        if (!this.searchText) {
            this.filteredCountries = this.countries;
        } else {
            const lowerSearch = this.searchText.toLowerCase();
            this.filteredCountries = this.countries.filter(c =>
                c.name.toLowerCase().includes(lowerSearch) ||
                c.dialCode.includes(lowerSearch) ||
                c.iso2.toLowerCase().includes(lowerSearch)
            );
        }
    }

    @HostListener('document:click', ['$event'])
    clickout(event) {
        if (!this.eRef.nativeElement.contains(event.target)) {
            this.isOpen = false;
        }
    }
}
